import { throttle } from 'lodash';
import FlatButton from '@mui/material/Button';
import React, { useCallback, useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import useIsMobile from 'lib/useIsMobile';
import Empty from 'components/Empty';
// import LoadingIndicator from 'components/LoadingIndicator';
import PreviousButton from 'ripple/Components/GridTileScroller/PreviousButton';
import NextButton from 'ripple/Components/GridTileScroller/NextButton';
import className from 'ripple/Components/GridTileScroller/index.scss';

const styles = {
  done: {
    marginTop: 20,
  },
  empty: {
    minHeight: '60vh',
    justifyContent: 'center',
    alignItems: 'center',
  },
};

const calculateTopPosition = el => (el ? el.offsetTop + calculateTopPosition(el.offsetParent) : 0);
const calculateOffset = el => calculateTopPosition(el)
  + (el.offsetHeight
    - (window ? window.pageYOffset : 0)
    - (window ? window.innerHeight : 0));
const calculateLeftPosition = el => (el ? el.offsetLeft + calculateLeftPosition(el.offsetParent) : 0);
const calculateHorizontalOffset = el => calculateLeftPosition(el)
  + (el.offsetWidth
    - (window ? window.pageXOffset : 0)
    - (window ? window.innerWidth : 0));

const GridTileScroller = ({
  children,
  done,
  // doneButtons,
  // doneIcon,
  // doneText,
  // doneTitle = 'That\'s everything!',
  empty,
  emptyIcon,
  emptyText,
  emptyTitle = "There's nothing here yet!",
  interval = 500,
  onEmptyClick,
  pageSize = 10,
  totalCount = 0,
  hasNextPage = false,
  loadNextPage,
  loading,
  // showDone = true,
  // showLoading = true,
  threshold = 500,
  scrollerId = 'scrollContainer',
  vertical = true,
  leftHandler,
}) => {
  const mobile = useIsMobile();
  const totalPages = useMemo(() => {
    return Math.ceil(totalCount / pageSize);
  }, [totalCount, pageSize]);
  const [currentPage, setCurrentPage] = useState(1);
  const canEnablePrevious = useMemo(() => {
    return !vertical && currentPage > 1;
  }, [vertical, currentPage]);
  const canEnableNext = useMemo(() => {
    return !vertical && currentPage < totalPages;
  }, [totalPages, vertical, currentPage]);
  const [hover, setHover] = useState(false);
  const [scrollContainer, setScrollContainer] = useState(null);
  const scrollTarget = useMemo(
    () => () => {
      let obj = window;
      if (scrollerId !== 'scrollContainer') {
        obj = document.getElementById(scrollerId);
      }
      return obj;
    },
    [scrollerId],
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleScroll = useCallback(
    throttle(() => {
      if (scrollContainer && !empty) {
        if (done && scrollContainer) {
          scrollContainer.removeEventListener('scroll', handleScroll);
        } else if (
          !loading
          && (vertical
            ? calculateOffset(scrollContainer)
            : calculateHorizontalOffset(scrollContainer)) <= threshold
        ) {
          loadNextPage();
        }
      }
    }, interval),
    [empty, done, loading, scrollContainer],
  );
  useEffect(() => {
    setScrollContainer(scrollTarget);
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
    }
    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', handleScroll);
      }
    };
  }, [
    handleScroll,
    scrollContainer,
    scrollTarget,
    scrollerId,
    setScrollContainer,
  ]);
  const scrollToElement = useCallback(
    (page, forward = true) => {
      let focusElement = 0;
      if (forward) {
        focusElement = page === totalPages ? totalCount : page * pageSize;
      } else {
        focusElement = (page - 1) * pageSize + 1;
      }
      setCurrentPage(page);
      const timer = setInterval(() => {
        const element = document.querySelector(
          `#${scrollerId} li:nth-child(${focusElement})`,
        );
        if (element) {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'start',
          });
          clearInterval(timer);
        }
      }, 100);
    },
    [pageSize, scrollerId, totalCount, totalPages],
  );
  const scrollLeft = useCallback(() => {
    scrollToElement(currentPage - 1, false);
  }, [scrollToElement, currentPage]);
  const scrollRight = useCallback(() => {
    if (hasNextPage) {
      loadNextPage(() => {
        scrollToElement(currentPage + 1);
      });
    } else {
      scrollToElement(currentPage + 1);
    }
  }, [currentPage, hasNextPage, loadNextPage, scrollToElement]);
  return empty ? (
    <Empty
      buttons={
        emptyText && (
          <FlatButton disabled={!onEmptyClick} onClick={onEmptyClick} secondary>
            {emptyText}
          </FlatButton>
        )
      }
      icon={emptyIcon}
      style={styles.empty}
      title={emptyTitle}
    />
  ) : (
    <div
      className={className.scrollStyle}
      style={{ position: 'relative', display: 'block', overflowY: 'auto' }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {!mobile && canEnablePrevious && (
        <PreviousButton
          hover={hover}
          onClick={scrollLeft}
          onMouseEnter={() => leftHandler && leftHandler()}
        />
      )}
      {children}
      {/* {showLoading && loading && !done && <LoadingIndicator />} */}
      {/* {showDone && !loading && done && vertical && (
          <Empty
          buttons={doneButtons}
          icon={doneIcon}
          style={styles.done}
          text={doneText}
          title={doneTitle}
          />
          )} */}
      {!mobile && canEnableNext && (
        <NextButton
          hover={hover}
          onClick={scrollRight}
          onMouseEnter={() => leftHandler && leftHandler()}
        />
      )}
    </div>
  );
};

GridTileScroller.propTypes = {
  children: PropTypes.node,
  done: PropTypes.bool,
  doneButtons: PropTypes.node,
  doneIcon: PropTypes.node,
  doneText: PropTypes.string,
  doneTitle: PropTypes.string,
  totalCount: PropTypes.number,
  hasNextPage: PropTypes.bool,
  empty: PropTypes.bool,
  emptyIcon: PropTypes.node,
  emptyText: PropTypes.string,
  emptyTitle: PropTypes.string,
  interval: PropTypes.number,
  onEmptyClick: PropTypes.func,
  pageSize: PropTypes.number,
  relay: PropTypes.object.isRequired,
  showDone: PropTypes.bool,
  threshold: PropTypes.number,
  loadNextPage: PropTypes.func,
  loading: PropTypes.bool,
  scrollerId: PropTypes.string,
  vertical: PropTypes.bool,
  leftHandler: PropTypes.func,
};

export default GridTileScroller;
